import { forwardRef, useMemo, useRef } from 'react';

import { a, useSpring } from '@react-spring/web';
import { useForkRef } from '@spazious/react-hooks';

export type TransitionProps<T extends React.ElementType> = React.ComponentProps<T> & {
  /**
   * The component used for the root node.
   */
  as?: T;
  /**
   * The duration of the transition, in milliseconds
   */
  timeout?: number;
  /**
   * If `true`, the component will transition in.
   */
  in?: boolean;
  /**
   * The style properties set when the component is entering.
   */
  enter?: React.CSSProperties;
  /**
   * The style properties set when the component is exiting.
   */
  exit?: React.CSSProperties;
  /**
   *
   */
  style?: React.CSSProperties;
  /**
   * Callback fired before the "entering" status is applied.
   * @param node - The component to be transitioned.
   * @param isAppearing - Is the component entering for the first time?
   */
  onEnter?: (node: React.ElementRef<T>, isAppearing: boolean) => void;
  /**
   * Callback fired before the "exiting" status is applied.
   * @param node - The component to be transitioned out.
   */
  onExit?: (node: React.ElementRef<T>) => void;
};

/**
 * The `Transition` component lets you define a transition component
 * tries to respect the react-transition-group api as much as possible
 * but it's not a 1:1 mapping
 *
 * @see https://reactcommunity.org/react-transition-group/transition#Transition-props
 */
export const Transition = forwardRef(
  <T extends React.ElementType>(
    {
      as = 'div' as T,
      in: inProp = true,
      enter = {},
      exit = {},
      timeout,
      style,
      onEnter,
      onExit,
      ...restProps
    }: TransitionProps<T>,
    ref: React.ForwardedRef<React.ElementRef<T>>
  ) => {
    const isAppearingRef = useRef(true);
    const nodeRef = useRef<React.ElementRef<any>>(null);
    const forkedRef = useForkRef<React.ElementRef<any>>(ref, nodeRef);
    const animatedStyle = useSpring({
      from: exit,
      to: inProp ? enter : exit,
      onStart: () => {
        if (onEnter && nodeRef?.current) {
          onEnter(nodeRef.current as any, isAppearingRef.current);
        }
        isAppearingRef.current = false;
      },
      onRest: () => {
        if (onExit && nodeRef?.current) {
          onExit(nodeRef.current as any);
        }
      },
      config: {
        duration: timeout,
      },
    });

    const C = useMemo(() => a(as), [as]);

    return <C ref={forkedRef} style={{ ...animatedStyle, ...style }} {...restProps} />;
  }
);
